import React from 'react';

import { notify } from '../lib/notifications';

/**
 * Application-wide error boundary.
 *
 * Render-time errors thrown deep in any tree would otherwise crash the
 * whole React app and leave the user with a blank page.  By wrapping the
 * top-level routes in this boundary we:
 *
 *   1. Surface the error in a toast (so it's visible alongside backend
 *      errors caught by the axios interceptor).
 *   2. Show a graceful fallback page with the message + a reload button.
 *   3. Keep the original error / stack on the console for the developer.
 *
 * The user's brief: "any and all errors that are there in the backend
 * should be displayed in the frontend as well, so that we can look into
 * it."  This component covers the *frontend* side of that — render-time
 * crashes, JSON-parsing errors, etc. — completing the picture.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error('[react] uncaught render error', error, info);
    try {
      notify.error(
        (error && (error.message || String(error))) || 'Render error',
        { title: 'Application error' },
      );
    } catch (_) {
      /* notify can't throw, but be defensive */
    }
  }

  reset = () => {
    this.setState({ error: null, info: null });
  };

  reload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="ty-fullscreen-center" role="alert">
        <div style={{ maxWidth: 720, padding: 24 }}>
          <h2 style={{ marginTop: 0 }}>Something went wrong.</h2>
          <p>The app hit an unexpected error and couldn't continue rendering.</p>
          <pre className="ty-error__pre">
            {String(error.stack || error.message || error)}
          </pre>
          {info && info.componentStack && (
            <details style={{ marginTop: 12 }}>
              <summary>Component stack</summary>
              <pre className="ty-error__pre">{info.componentStack}</pre>
            </details>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button type="button" className="ty-btn" onClick={this.reload}>
              Reload page
            </button>
            <button type="button" className="ty-btn ty-btn--ghost" onClick={this.reset}>
              Try to recover
            </button>
          </div>
        </div>
      </div>
    );
  }
}
